// ═══════════════════════════════════════════════════════════════
// AAMVA Driver's License barcode parser
// US driver's licenses encode personal data as a PDF417 barcode
// using the AAMVA DL/ID Card Design Standard.
// Reference: https://www.aamva.org/identity/barcode-standard/
// ═══════════════════════════════════════════════════════════════

export interface DLData {
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  dateOfBirth: string;       // ISO YYYY-MM-DD
  sex: string;               // 'M' | 'F' | ''
  address: string;
  city: string;
  state: string;
  zip: string;
  licenseNumber: string;
  issueDate: string;         // ISO
  expirationDate: string;    // ISO
  height: string;
  weight: string;
  eyeColor: string;
  raw: Record<string, string>;
}

// MMDDCCYY → YYYY-MM-DD
function parseAamvaDate(s: string): string {
  if (!s || s.length !== 8) return '';
  const mm = s.slice(0, 2);
  const dd = s.slice(2, 4);
  const yyyy = s.slice(4, 8);
  if (!/^\d{8}$/.test(s)) return '';
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parse an AAMVA PDF417 driver's license barcode payload.
 * Returns null if the input doesn't look like an AAMVA barcode.
 */
export function parseDriversLicense(raw: string): DLData | null {
  if (!raw) return null;

  // AAMVA barcodes start with @\n\u001e\rANSI followed by IIN + version + jurisdiction.
  // Tolerant: just look for "ANSI " header anywhere in the first 50 chars.
  if (!raw.includes('ANSI ')) return null;

  // Each data element is `<3-letter code><value>` separated by line breaks (\n or \r).
  // Some readers include leading characters before the first DL element.
  const lines = raw.split(/[\r\n]+/).filter(Boolean);
  const fields: Record<string, string> = {};
  for (const line of lines) {
    // Strip any leading non-alpha characters (some readers prefix \u001e or similar)
    const cleaned = line.replace(/^[^A-Z]+/, '');
    const m = cleaned.match(/^([A-Z]{3})(.*)$/);
    if (m) {
      const code = m[1];
      const val = m[2].trim();
      // Don't overwrite if already set (first occurrence wins, AAMVA can repeat headers)
      if (!fields[code]) fields[code] = val;
    }
  }

  // Field code mapping. Various AAMVA versions use different codes for the same data;
  // try common alternatives and pick the first non-empty.
  const pick = (...codes: string[]) => {
    for (const c of codes) if (fields[c]) return fields[c];
    return '';
  };

  const firstName  = pick('DAC', 'DCT');                    // first / given name
  const middleName = pick('DAD');                            // middle / additional name
  const lastName   = pick('DCS', 'DAB');                    // family name
  const fullName   = pick('DAA') || `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();

  const dateOfBirth     = parseAamvaDate(pick('DBB'));
  const issueDate       = parseAamvaDate(pick('DBD'));
  const expirationDate  = parseAamvaDate(pick('DBA'));

  const sex             = (() => {
    const v = pick('DBC');
    if (v === '1') return 'M';
    if (v === '2') return 'F';
    return v || '';
  })();

  const address  = pick('DAG');
  const city     = pick('DAI');
  const state    = pick('DAJ');
  const zip      = pick('DAK').replace(/[^0-9-]/g, '').slice(0, 10);

  const licenseNumber = pick('DAQ');
  const height        = pick('DAU');
  const weight        = pick('DAW');
  const eyeColor      = pick('DAY');

  // Sanity check — at least one identity field must be present
  if (!lastName && !fullName && !licenseNumber) return null;

  return {
    firstName,
    middleName,
    lastName,
    fullName,
    dateOfBirth,
    sex,
    address,
    city,
    state,
    zip,
    licenseNumber,
    issueDate,
    expirationDate,
    height,
    weight,
    eyeColor,
    raw: fields,
  };
}
